import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule, RouterOutlet, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { MenuModule } from 'primeng/menu';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { SessionsModule } from '../../modules/besharp-sessions/besharp-sessions.module';
import {
  BesharpSidebarComponent,
  BesharpSidebarConfig,
  BesharpSidebarItem,
} from '../../modules/besharp-sidebar/sidebar/component/besharp-sidebar.component';
import { BesharpTopbarComponent, BesharpTopbarItems } from '../../modules/besharp-topbar/besharp-topbar.component';
import { SessionsService } from '../../modules/besharp-sessions/lib/services/sessions.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ChatApiService, SessionInfo } from '../../services/chat-api.service';
import { DatePipe } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Button } from 'primeng/button';
import {
  mentionPlainText,
  renderMentionPreview,
} from '../../modules/besharp-chat/pipes/mention-chip';

@Component({
  selector: 'app-base-layout',
  templateUrl: './base-layout.component.html',
  styleUrl: './base-layout.component.scss',
  providers: [MessageService, ConfirmationService],
  imports: [
    RouterOutlet,
    RouterModule,
    MenuModule,
    BesharpSidebarComponent,
    BesharpTopbarComponent,
    SessionsModule,
    DividerModule,
    SelectModule,
    ToastModule,
    DatePipe,
    SkeletonModule,
    ConfirmDialog,
    Button,
  ],
})
export class BaseLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly router: Router = inject(Router);
  private readonly titleService: Title = inject(Title);
  private readonly sessionsService = inject(SessionsService);
  private readonly messageService = inject(MessageService);
  private readonly chatApiService = inject(ChatApiService);
  private readonly confirmationService = inject(ConfirmationService);
  // Topbar Settings
  protected title = 'Datahub AI';
  protected logo = '/images/logo.svg';
  protected homeRoute = '/app';
  protected topBarItems!: BesharpTopbarItems;
  protected sidebarLoaderItems = [0, 1, 2, 3, 4, 5];

  // Sidebar Settings
  protected sidebarConfiguration!: BesharpSidebarConfig;
  protected sidebarSize: string = '200px';
  private hasViewInitialized: boolean = false;
  @ViewChild('contentContainer') contentContainer!: ElementRef<HTMLDivElement>;

  // Route title mapping
  private readonly routeTitleMap: { [key: string]: string } = {
    '': 'Home',
  };
  loadingSessions: boolean = true;
  protected sessions: SessionInfo[] = [];
  protected selectedSessionId?: string;
  protected readonly maxSessionLabelLength = 30;
  private readonly sanitizer = inject(DomSanitizer);
  protected isBackendConnected = false;
  protected isCheckingBackendHealth = false;
  private healthCheckIntervalId?: number;
  private newSessionSub?: Subscription;
  Upload: any;

  sidebarSizeChanged(event: string): void {
    this.sidebarSize = event;
    if (this.contentContainer) {
      const element = this.contentContainer.nativeElement;
      const shouldDisableTransition = !this.hasViewInitialized;
      if (shouldDisableTransition) {
        element.style.transition = 'none';
      }
      if (event === '0' || event === '0px') {
        element.style.width = '';
        element.style.marginLeft = '0';
      } else {
        element.style.width = `calc(100% - ${event})`;
        element.style.marginLeft = event;
      }
      if (shouldDisableTransition) {
        requestAnimationFrame(() => {
          element.style.transition = '';
        });
      }
    }
  }

  constructor() {}

  async ngOnInit(): Promise<void> {
    this.updatePageTitle(this.router.url);
    this.syncSelectedSessionFromUrl(this.router.url);
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.updatePageTitle(event.urlAfterRedirects);
      this.syncSelectedSessionFromUrl(event.urlAfterRedirects);
    });

    this.topBarItems = {
      itemsSize: 'xs',
      items: [
        {
          icon: 'pi pi-sun',
          iconPosition: 'left',
          outlined: true,
          command: () => this.toggleDarkMode(),
          severity: 'success',
        },
        {
          icon: 'pi pi-sign-out',
          iconPosition: 'left',
          outlined: true,
          command: () => this.goToSessionSelector(),
          severity: 'secondary',
        },
      ],
    };

    this.loadingSessions = true;
    this.sidebarConfiguration = {
      foldable: true,
      loading: true,
      items: [],
    };

    this.newSessionSub = this.chatApiService.newSessionCreated$.subscribe(async (sessionId) => {
      this.selectedSessionId = sessionId;
      await this.loadSessions();
    });

    await this.checkBackendHealth();
    this.startHealthCheckPolling();
    await this.loadSessions();
  }

  ngAfterViewInit(): void {
    if (this.contentContainer) {
      const element = this.contentContainer.nativeElement;
      element.style.transition = 'none';
      element.style.width = `calc(100% - ${this.sidebarSize})`;
      element.style.marginLeft = this.sidebarSize;
      requestAnimationFrame(() => {
        element.style.transition = '';
        this.hasViewInitialized = true;
      });
    }
  }

  ngOnDestroy(): void {
    if (this.healthCheckIntervalId !== undefined) {
      window.clearInterval(this.healthCheckIntervalId);
      this.healthCheckIntervalId = undefined;
    }
    this.newSessionSub?.unsubscribe();
  }

  private updatePageTitle(url: string): void {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const pageName = this.routeTitleMap[cleanUrl];
    if (pageName) {
      this.titleService.setTitle(`Datahub AI - ${pageName}`);
    } else {
      this.titleService.setTitle('Datahub AI');
    }
  }

  private async loadSessions(): Promise<void> {
    this.loadingSessions = true;
    this.sidebarConfiguration = { ...this.sidebarConfiguration, loading: true };
    try {
      this.sessions = await this.chatApiService.listSessions();
      this.sidebarConfiguration = {
        foldable: true,
        loading: false,
        items: [
          ...this.sessions
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .map((session) => ({
              label: session.first_message,
              link: `/app/session/${session.session_id}`,
              icon: 'pi pi-comment',
              session: session,
              active: session.session_id === this.selectedSessionId,
              useTemplate: true,
            })),
        ],
      };
    } catch (_error) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load sessions.' });
    } finally {
      this.sidebarConfiguration = { ...this.sidebarConfiguration, loading: false };
      this.loadingSessions = false;
    }
  }

  protected async refreshSessions(): Promise<void> {
    await this.loadSessions();
    await this.checkBackendHealth();
  }

  protected async refreshBackendHealth(): Promise<void> {
    await this.checkBackendHealth();
  }

  protected isSessionSelected(item: { session?: SessionInfo }): boolean {
    return item?.session?.session_id === this.selectedSessionId;
  }

  protected getSessionLabel(item: { label?: string }): string {
    // The stored first message carries the wire form of any pinned reference; the reader
    // should see the label, not the brackets and the code.
    return mentionPlainText(item?.label ?? 'New Conversation');
  }

  /** Same preview with references as chips, cut to the visible length of the row. */
  protected getSessionLabelHtml(item: { label?: string }): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      renderMentionPreview(item?.label ?? 'New Conversation', this.maxSessionLabelLength),
    );
  }

  protected confirmSessionSwitch(item: BesharpSidebarItem & { session?: SessionInfo }): void {
    const targetSessionId = item?.session?.session_id;
    if (targetSessionId && !this.selectedSessionId) {
      this.router.navigateByUrl(item.link!);
      return;
    }
    if (!targetSessionId || !this.selectedSessionId || this.selectedSessionId === targetSessionId) {
      return;
    }

    this.confirmationService.confirm({
      message: `Are you sure you want to change the current session?`,
      header: 'Change Session',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        severity: 'danger',
        label: 'Change Session',
      },
      accept: () => {
        this.router.navigateByUrl(item.link!);
      },
    });
  }

  protected confirmSessionDeletion(item: BesharpSidebarItem & { session?: SessionInfo }) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete this session? This action cannot be undone.`,
      header: 'Delete Session',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        severity: 'danger',
        label: 'Delete Session',
      },
      accept: async () => {
        try {
          await this.chatApiService.deleteSession(item.session!.session_id);
          this.messageService.add({
            severity: 'success',
            summary: 'Session Deleted',
            detail: 'The session has been deleted successfully.',
          });
          this.sessions.splice(
            this.sessions.findIndex((s) => s.session_id === item.session!.session_id),
            1
          );
          this.sidebarConfiguration = {
            foldable: true,
            loading: false,
            items: [
              ...this.sessions
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((session) => ({
                  label: session.first_message,
                  link: `/app/session/${session.session_id}`,
                  icon: 'pi pi-comment',
                  session: session,
                  active: session.session_id === this.selectedSessionId,
                  useTemplate: true,
                })),
            ],
          };
          if (item.session!.session_id === this.selectedSessionId) {
            this.router.navigate(['/app']);
          }
        } catch (_error) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete session.' });
        }
      },
    });
  }

  private syncSelectedSessionFromUrl(url: string): void {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const match = cleanUrl.match(/\/app\/session\/([^/]+)/);
    this.selectedSessionId = match?.[1];

    if (!this.sidebarConfiguration?.items?.length) {
      return;
    }

    this.sidebarConfiguration = {
      ...this.sidebarConfiguration,
      items: this.sidebarConfiguration.items.map((item) => ({
        ...item,
        active: (item['session'] as SessionInfo | undefined)?.session_id === this.selectedSessionId,
      })),
    };
  }

  private startHealthCheckPolling(): void {
    if (this.healthCheckIntervalId !== undefined) {
      window.clearInterval(this.healthCheckIntervalId);
    }

    this.healthCheckIntervalId = window.setInterval(() => {
      void this.checkBackendHealth();
    }, 10000);
  }

  private async checkBackendHealth(): Promise<void> {
    this.isCheckingBackendHealth = true;
    try {
      await this.chatApiService.checkHealth();
      this.isBackendConnected = true;
    } catch {
      this.isBackendConnected = false;
    } finally {
      this.isCheckingBackendHealth = false;
    }
  }

  toggleDarkMode(): void {
    const element = document.querySelector('html');
    element!.classList.toggle('dark-mode');
    const isDark = element!.classList.contains('dark-mode');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  }

  goToSessionSelector(): void {
    const sessions = this.sessionsService.getSessions();
    sessions.forEach((session) => {
      this.sessionsService.removeSession(session.id);
    });

    this.router.navigate(['/session/signin']);
  }

  goToUploadSection() {
    this.router.navigate(['/upload']);
  }
}
